import React, { createContext, useContext, useState, useEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface User {
  email:    string;
  fullName: string;
  mobile:   string;
  role:     'admin' | 'viewer' | 'guest';
  tenantId: string | null;
}

interface AuthCtx {
  user:        User | null;
  accessToken: string | null;
  sendOtp:     (fullName: string, email: string, mobile: string) => Promise<void>;
  verifyOtp:   (email: string, otp: string) => Promise<void>;
  loginAsGuest: () => void;
  logout:      () => void;
}

const AuthContext = createContext<AuthCtx>({} as AuthCtx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser]               = useState<User | null>(() => {
    try { return JSON.parse(localStorage.getItem('cf_user') || 'null'); } catch { return null; }
  });
  const [accessToken, setAccessToken] = useState<string | null>(
    () => localStorage.getItem('cf_token')
  );

  useEffect(() => {
    if (user)        localStorage.setItem('cf_user',  JSON.stringify(user));
    else             localStorage.removeItem('cf_user');
    if (accessToken) localStorage.setItem('cf_token', accessToken);
    else             localStorage.removeItem('cf_token');
  }, [user, accessToken]);

  const sendOtp = async (fullName: string, email: string, mobile: string) => {
    const res  = await fetch(`${API_BASE}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fullName, email, mobile }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to send OTP');
  };

  const verifyOtp = async (email: string, otp: string) => {
    const res  = await fetch(`${API_BASE}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.error || 'Invalid OTP');
    setAccessToken(data.data.token);
    setUser({
      email: data.data.user.email,
      fullName: data.data.user.fullName,
      mobile: '',
      role: data.data.user.role || 'viewer',
      tenantId: data.data.user.tenantId || null,
    });
    // Save plan from backend
    if (data.data.user.plan) {
      localStorage.setItem('cf_plan', data.data.user.plan);
    }
  };

  const loginAsGuest = () => {
    setUser({ email: 'guest@cookiefactory.com', fullName: 'Guest', mobile: '', role: 'guest', tenantId: null });
    setAccessToken(null);
  };

  const logout = () => {
    setUser(null);
    setAccessToken(null);
    localStorage.removeItem('cf_plan');
    localStorage.removeItem('cf_twins_month');
    localStorage.removeItem('cf_user');
    localStorage.removeItem('cf_token');
  };

  return (
    <AuthContext.Provider value={{ user, accessToken, sendOtp, verifyOtp, loginAsGuest, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
